import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Store scroll positions by route key
const scrollPositions = new Map<string, number>();

/**
 * Smart scroll restoration that:
 * - Restores scroll position on back/forward navigation
 * - Resets to top on new navigation (push)
 * - Doesn't cause layout shift by using RAF
 */
export const SmartScrollRestoration: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousKeyRef = useRef<string | null>(null);
  const isRestoringRef = useRef(false);

  // Save scroll position before leaving current page
  const saveScrollPosition = useCallback(() => {
    if (previousKeyRef.current && !isRestoringRef.current) {
      scrollPositions.set(previousKeyRef.current, window.scrollY);
    }
  }, []);

  // Handle scroll restoration/reset
  useEffect(() => {
    const currentKey = location.key;
    
    // Save previous position before updating ref
    saveScrollPosition();

    // Determine scroll behavior based on navigation type
    const handleScroll = () => {
      isRestoringRef.current = true;

      if (navigationType === 'POP') {
        // Back/forward navigation - restore saved position
        const savedPosition = scrollPositions.get(currentKey);
        if (savedPosition !== undefined) {
          // Use RAF to avoid blocking and ensure DOM is ready
          requestAnimationFrame(() => {
            window.scrollTo({
              top: savedPosition,
              behavior: 'instant' // No animation to avoid CLS
            });
            isRestoringRef.current = false;
          });
        } else {
          isRestoringRef.current = false;
        }
      } else {
        // New navigation (PUSH/REPLACE) - scroll to top
        requestAnimationFrame(() => {
          window.scrollTo({
            top: 0,
            behavior: 'instant'
          });
          isRestoringRef.current = false;
        });
      }
    };

    // Small delay to ensure route transition has started
    const timer = setTimeout(handleScroll, 0);
    
    // Update ref after handling
    previousKeyRef.current = currentKey;

    return () => clearTimeout(timer);
  }, [location.key, navigationType, saveScrollPosition]);

  // Save position on beforeunload and route changes
  useEffect(() => {
    const handleBeforeUnload = () => saveScrollPosition();
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      saveScrollPosition();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveScrollPosition]);

  return null;
};
